import * as ng from 'angular';
import * as _ from 'underscore';
import * as Highcharts from 'highcharts';

import {ReportsService, Dataset} from '..';

ng.module('e360')
   .factory('mapChartFactory', mapChartFactory);

mapChartFactory.$inject = ['instanceServiceFactory'];
function mapChartFactory(instanceServiceFactory) {
   return instanceServiceFactory(MapChart);
}

// Requires that object data has properties named countryCode stateCode and total
export class MapChart {
   static $inject = ['$q', 'ReportsService', 'countries', 'ds'];

   options:any;
   chart:Highcharts.ChartObject;

   constructor(
      private $q:ng.IQService,
      private reportsService:ReportsService,
      private countries,
      private ds:Dataset
   ) {
      this.options = {
         chart: {
            events: {
               load: chartOnLoad,
               drilldown: chartOnDrillDown,
               drillup: chartOnDrillUp
            }
         },

         title : {
            text: ds.title
         },

         legend: {
            layout: 'vertical',
            align: 'right',
            verticalAlign: 'middle'
         },

         colorAxis: {
            min: 0,
            stops: [
               [0, '#C7ECFC'],
               [0.3, '#3CB4E5'],
               [0.6, '#39329c'],
               [0.9, '#E353A9']
           ]
         },

         mapNavigation: {
            enabled: true,
            buttonOptions: {
               verticalAlign: 'bottom'
            }
         },

         plotOptions: {
            map: {
               states: {
                  hover: {
                     color: '#15ca9d'
                  }
               }
            }
         },

         series : [{
            data : [],
            name: 'Participants',
            mapData: Highcharts.maps['custom/world-lowres'],
            joinBy: 'iso-a2',
            dataLabels: {
               enabled: true,
               color: '#fff',
               format: '{point.value}',
               style: {
                  textShadow: 'rgba(0, 0, 0, 0.9) 0px 0px 3px'
               }
            }
         }],

         lang: {
            drillUpText: "<strong>&lt;</strong> World View"
         },

         drilldown: {
            activeDataLabelStyle: {
               color: '#FFFFFF',
               textDecoration: 'none',
               textShadow: '0 0 3px #000000'
            },
            drillUpButton: {
               position: {
                  relativeTo: 'spacingBox',
                  align: 'left',
                  x: 0
               }
            }
         }
      };

      function chartOnLoad(e) {
         // load first country in the results
         this.series[0].data[0].firePointEvent('click');
      }

      function chartOnDrillDown(e) {
         if (e.point.states.length === 0) {
            return;
         }

         if (e.seriesOptions) {
            return;
         }

         this.addSeriesAsDrilldown(e.point, {
            mapData: Highcharts.maps[e.point.drilldown],
            name: 'Participants',
            data: e.point.states,
            dataLabels: {
               enabled: true,
               color: '#fff',
               format: '{point.value}',
               style: {
                  textShadow: 'rgba(0, 0, 0, 0.9) 0px 0px 3px'
               }
            },
            joinBy: 'iso'
         });
      }

      function chartOnDrillUp() {
         this.setTitle(null, { text: 'World Map' });
      }
   }

   setData(data:any[]) {
      data = this.scrubData(data);

      return this.lazyLoadGeoData(data).then(() => {
         let opts = _.clone(this.options);
         data.sort((a, b) => b.total - a.total);
         opts.series[0].data = this.formatData(data);
         this.chart = new Highcharts.Map(this.ds.id, opts);
      });
   }

   setColors(colors:string[]) {
      this.options.colorAxis = {
         min: 0,
         stops: [
            [0, colors[0]],
            [0.3, colors[1]],
            [0.6, colors[2]],
            [0.9, colors[3]]
         ]
      };
   }

   /**
    * Filter data to only include country data that is supported.
    * Under normal use cases data will contain valid iso3166 country codes
    * for only the countries that we have populated in the database, but
    * some areas of the system allow free-form input of country code so we
    * need to scrub data of country codes that are not supported.
    */
   private scrubData(data:any[]) {
      return _.filter(data, (record) => {
         if (record.countryCode) {
            return ng.isDefined(this.countries[record.countryCode.toUpperCase()]);
         }

         return false;
      });
   }

   /**
    * Load map for each unique county in the report data returning a promise
    * for the cached geo.json data..
    * If the country has been previously loaded a promise is not returned.
    */
   private lazyLoadGeoData(data) {
      // extract unique lower case country codes from report data
      let countryCodes = _.chain(data)
         .map(function (record) {
            return record.countryCode.toLowerCase();
         })
         .uniq()
         .value();

      let promises = _.chain(countryCodes)
         // don't load the same country map data twice
         .filter((code) => !Highcharts.maps[code])
         .map((code) => {
            return this.reportsService.getMapData(code).then((response) => {
               Highcharts.maps[code] = response.data;
               return Highcharts.maps[code];
            });
         }).value();

      return this.$q.all(promises);
   }

   private formatData(data:any[]) {
      // grouping data by country code gives us unique country codes as
      // keys of the data object, and make it easy to aggregate the data
      let dataByCountry = _.groupBy(data, 'countryCode');

      return _.map(_.keys(dataByCountry), (countryCode) => {
         return {
            // use iso3166 alpha-2 which matches our country data
            'iso-a2': countryCode,

            drilldown: countryCode.toLowerCase(),

            // get the total participants for the country
            value: _.reduce(dataByCountry[countryCode], (total, record) => {
               return total + record.total;
            }, 0),

            states: _.map(dataByCountry[countryCode], (record) => {
               return {
                  // highcharts doesn't have anything at the feature level that
                  // reliably matches iso3166 region codes so we've added our
                  // own property named 'iso' which is populated by a tool that
                  // downloads geo.json files for the various supported countries
                  iso: record.stateCode,
                  value: record.total
               };
            })
         };
      });
   }

   onLoadStart() { }

   onLoadFinish() { }
}

export interface MapChartFactory {
   ({ds:Dataset}):MapChart;
}
